import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Layout from '../../components/feature/Layout';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signIn, signUp, loading: authLoading, authError, hardResetAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');

  // ログイン済みの場合はリダイレクト
  useEffect(() => {
    // 🔄 認証状態の初期化が終わっていない間は何もしない
    if (authLoading) return;

    if (user) {
      const redirect = searchParams.get('redirect') || '/account/my-recipes';
      console.log('[Login] user detected, navigating after login', user.id, '→', redirect);
      navigate(redirect);
    }
  }, [user, authLoading, navigate, searchParams]);

  // ログイン処理
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください。');
      return;
    }

    try {
      setLoading(true);
      const result = await signIn(email, password);

      if (!result.ok) {
        setError(result.error || 'ログインに失敗しました。');
        return;
      }

      // navigate は削除（useEffect に任せる）
    } catch (err) {
      console.error('ログインエラー:', err);
      setError('ログインに失敗しました。時間をおいてもう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // 新規登録処理
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // バリデーション
    if (!email || !password || !confirmPassword) {
      setError('すべての項目を入力してください。');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('有効なメールアドレスを入力してください。');
      return;
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください。');
      return;
    }

    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return;
    }

    try {
      setLoading(true);
      const result = await signUp(email, password);

      if (!result.ok) {
        setError(result.error || '登録に失敗しました。');
        return;
      }

      // 成功時
      setSuccessMessage('登録を受け付けました。メールを確認し、完了後にこの画面からログインしてください。');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setActiveTab('login');
    } catch (err) {
      console.error('登録エラー:', err);
      setError('登録に失敗しました。時間をおいてもう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // 強制リセット処理
  const handleHardReset = async () => {
    try {
      await hardResetAuth();
      setError('');
      setSuccessMessage('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('リセットエラー:', err);
    }
  };

  // 認証情報取得中
  if (authLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <p className="text-gray-500">読み込み中…</p>
        </div>
      </Layout>
    );
  }

  // ログイン済み（リダイレクト前）
  if (user) {
    return null;
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8">
            {/* ページタイトル */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">キョウコレにログイン</h1>
            </div>

            {/* 認証エラーメッセージ（AuthContext から） */}
            {authError && (
              <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <p className="mb-2">{authError}</p>
                <button
                  type="button"
                  onClick={handleHardReset}
                  className="inline-flex items-center rounded-md border border-red-300 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                  disabled={authLoading}
                >
                  <i className="ri-refresh-line mr-1"></i>
                  ログイン状態をリセットする
                </button>
              </div>
            )}

            {/* タブ */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('login');
                  setError('');
                  setSuccessMessage('');
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'login'
                    ? 'border-b-2 border-teal-600 text-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                ログイン
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('signup');
                  setError('');
                  setSuccessMessage('');
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'signup'
                    ? 'border-b-2 border-teal-600 text-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                新規登録
              </button>
            </div>

            {/* エラーメッセージ */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                {error}
              </div>
            )}

            {/* 成功メッセージ */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                {successMessage}
              </div>
            )}

            {/* ログインフォーム */}
            {activeTab === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
                    メールアドレス
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                    パスワード
                  </label>
                  <input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? 'ログイン中...' : 'ログイン'}
                </button>
              </form>
            )}

            {/* 新規登録フォーム */}
            {activeTab === 'signup' && (
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1">
                    メールアドレス
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1">
                    パスワード（6文字以上）
                  </label>
                  <input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                    パスワード（確認用）
                  </label>
                  <input
                    id="signup-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {loading ? '登録中...' : '新規登録'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
